import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { Clock, Users, ArrowLeft, ChefHat, Lightbulb } from "lucide-react";
import { LogoMark } from "@/components/dw/Logo";

export default function RecipeDetail() {
  const { slug } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/recipes/${slug}`).then(async (r) => {
      if (!r.ok) { setNotFound(true); setLoading(false); return; }
      setRecipe(await r.json());
      setLoading(false);
    }).catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: "var(--bg)" }}>
      <ChefHat size={32} style={{ color: "var(--red)" }} />
      <h1 className="mt-3 font-display text-2xl" style={{ color: "var(--primary)" }}>Recipe not found</h1>
      <Link to="/recipes" className="mt-4 btn-primary">Back to all recipes</Link>
    </div>
  );

  const totalTime = (recipe.prep_time_min || 0) + (recipe.cook_time_min || 0);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark size={36} />
            <span className="font-display text-xl" style={{ color: "var(--primary)" }}>DietWalaa</span>
          </Link>
          <Link to="/recipes" className="text-xs font-bold flex items-center gap-1" style={{ color: "var(--primary)" }}>
            <ArrowLeft size={12} /> All recipes
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 pt-10 pb-16">
        <div className="section-eyebrow">{recipe.category}</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl leading-tight" style={{ color: "var(--primary)" }}>
          {recipe.name}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm" style={{ color: "var(--text-secondary)" }}>
          {totalTime > 0 && <span className="flex items-center gap-1.5"><Clock size={14} /> {totalTime} min</span>}
          {recipe.servings > 0 && <span className="flex items-center gap-1.5"><Users size={14} /> {recipe.servings} serves</span>}
          {recipe.credit && <span className="flex items-center gap-1.5"><ChefHat size={14} /> {recipe.credit}</span>}
        </div>
        <div className="mt-3 flex flex-wrap gap-1">
          {(recipe.tags || []).map((t, i) => (
            <span key={i} className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--yellow-soft)", color: "var(--primary)" }}>{t}</span>
          ))}
        </div>

        <div className="mt-10 grid gap-10 md:grid-cols-5">
          <section className="md:col-span-2">
            <h2 className="font-display text-xl mb-3" style={{ color: "var(--primary)" }}>Ingredients</h2>
            <ul className="space-y-2">
              {(recipe.ingredients || []).map((it, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <span style={{ color: "var(--red)" }}>•</span>
                  <span style={{ color: "var(--text-primary)" }}>{it}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="md:col-span-3">
            <h2 className="font-display text-xl mb-3" style={{ color: "var(--primary)" }}>Method</h2>
            <ol className="space-y-4">
              {(recipe.steps || []).map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full font-bold text-xs flex items-center justify-center" style={{ background: "var(--primary)", color: "var(--bg)" }}>
                    {i + 1}
                  </span>
                  <p className="text-[15px] leading-relaxed" style={{ color: "var(--text-primary)" }}>{s}</p>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {recipe.tips && (
          <div className="mt-10 p-5 rounded-2xl flex gap-3" style={{ background: "var(--yellow-soft)", border: "1.5px solid var(--yellow-deep)" }}>
            <Lightbulb size={18} className="flex-shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
            <div>
              <div className="font-bold" style={{ color: "var(--primary)" }}>Dietitian's tip</div>
              <p className="mt-1 text-sm" style={{ color: "var(--text-primary)" }}>{recipe.tips}</p>
            </div>
          </div>
        )}

        <div className="mt-12 p-6 rounded-2xl text-center" style={{ background: "var(--primary)", color: "var(--bg)" }}>
          <p className="font-display text-2xl leading-tight">Want a personalised plan built around meals like this?</p>
          <Link to="/#contact" className="inline-block mt-4 btn-primary text-sm" style={{ background: "var(--yellow)", color: "var(--primary)" }}>
            Book a free consultation
          </Link>
        </div>
      </article>

      <footer className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-3xl mx-auto px-4 py-6 text-xs text-center" style={{ color: "var(--text-secondary)" }}>
          Recipe authored by {recipe.credit || "Dt Manthan Sharma"} · © Dietwalaa
        </div>
      </footer>
    </div>
  );
}
